import { useState, useEffect } from "react";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { format } from "date-fns";
import { TimeValue } from "react-aria";
import { Time } from "@internationalized/date";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { TimePicker } from "@/components/ui/time-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface ScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSchedule: (scheduledAt: Date) => void;
  entityType?: string;
  initialDate?: Date;
  loading?: boolean;
}

export function ScheduleModal({
  isOpen,
  onClose,
  onSchedule,
  entityType,
  initialDate,
  loading = false,
}: ScheduleModalProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    initialDate ?? undefined
  );
  const [selectedTime, setSelectedTime] = useState<TimeValue | null>(
    initialDate
      ? (new Time(initialDate.getHours(), initialDate.getMinutes()) as unknown as TimeValue)
      : (new Time(9, 0) as unknown as TimeValue)
  );
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialDate) {
        setSelectedDate(initialDate);
        setSelectedTime(new Time(initialDate.getHours(), initialDate.getMinutes()) as unknown as TimeValue);
      } else {
        // Default to tomorrow at 9:00 AM
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(9, 0, 0, 0);
        setSelectedDate(tomorrow);
        setSelectedTime(new Time(9, 0) as unknown as TimeValue);
      }
    }
  }, [isOpen, initialDate]);

  const handleSave = () => {
    if (!selectedDate || !selectedTime) return;

    const scheduledAt = new Date(selectedDate);
    const time = selectedTime as unknown as Time;
    scheduledAt.setHours(time.hour, time.minute, 0, 0);
    onSchedule(scheduledAt);
  };

  const entityLabel = entityType ?? "";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-sm rounded-2xl p-6"
        hideCloseButton
      >
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-xl font-semibold">
            Schedule {entityLabel}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            The time you select is based on teen's timezone.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Date Picker */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-500">
              <CalendarIcon className="h-4 w-4" />
              Date
            </label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "flex h-12 w-full items-center rounded-xl border border-gray-200 bg-white px-4 text-left text-base",
                    !selectedDate && "text-gray-400"
                  )}
                >
                  {selectedDate ? format(selectedDate, "yyyy-MM-dd") : "Select date"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    setSelectedDate(date);
                    setCalendarOpen(false);
                  }}
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time Picker */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="h-4 w-4" />
              Time
            </label>
            <TimePicker
              value={selectedTime}
              onChange={setSelectedTime}
              className="h-12 rounded-xl border-gray-200 text-base"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1 rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedDate || !selectedTime || loading}
            className="flex-1 rounded-xl bg-black text-white hover:bg-gray-800"
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
